import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CampaignHeader from '@/components/campaign-detail/CampaignHeader';
import CampaignStatsCards from '@/components/campaign-detail/CampaignStatsCards';
import CampaignOperationsTabs from '@/components/campaign-detail/CampaignOperationsTabs';
import CampaignFinancialTabs from '@/components/campaign-detail/CampaignFinancialTabs';
import AddInfluencerModal from '@/components/campaign-detail/AddInfluencerModal';
import { AddInfluencerModal as AddInfluencerByHandleModal } from '@/components/AddInfluencerModal';
import { ImportInfluencersModal } from '@/components/ImportInfluencersModal';
import EditCampaignModal from '@/components/campaign-detail/EditCampaignModal';
import { trackEvent } from '@/lib/mixpanel';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/currency';

import { useCampaignWithContact } from '../../hooks/useCampaignWithContact';
import { useCampaignInfluencers } from '../../hooks/useCampaignInfluencers';
import { mapDbRowToCampaignDetail } from '../../services/campaign.mappers';

export interface CampaignDetailViewProps {
  campaignId?: string;
}

export const CampaignDetailView: React.FC<CampaignDetailViewProps> = ({ campaignId: propCampaignId }) => {
  const navigate = useNavigate();
  const { userCountry } = useAuth();
  const { id: paramId } = useParams();
  const campaignId = propCampaignId || paramId;

  const mockExpenses = [
    {
      id: 'e1',
      date: '2025-01-15',
      amount: formatCurrency(10000, userCountry),
      category: 'Logistics',
      description: 'Shipment to 5 influencers',
      vendor: 'ShipFast',
    },
    {
      id: 'e2',
      date: '2025-01-12',
      amount: formatCurrency(6500, userCountry),
      category: 'Props',
      description: 'Ring lights for product shoots',
    },
  ];

  const [addInfluencerModalOpen, setAddInfluencerModalOpen] = useState(false);
  const [addByHandleModalOpen, setAddByHandleModalOpen] = useState(false);
  const [importCsvModalOpen, setImportCsvModalOpen] = useState(false);
  const [editCampaignModalOpen, setEditCampaignModalOpen] = useState(false);

  // Fetch real campaign data with contact information
  const { data: campaign, isLoading: loadingCampaign, error: campaignError } = useCampaignWithContact(
    campaignId || ''
  );

  // Fetch real campaign influencers data
  const { data: campaignInfluencers = [], isLoading: loadingInfluencers } = useCampaignInfluencers(
    campaignId || ''
  );

  useEffect(() => {
    if (campaign) {
      trackEvent('Campaign Viewed', {
        campaign_id: campaign.id,
        campaign_name: campaign.name,
        campaign_status: campaign.status,
      });
    }
  }, [campaign]);

  // Transform the data for the components
  const transformedInfluencers = campaignInfluencers.map((ci: any) => ({
    id: ci.influencers?.id,
    name: ci.influencers?.name,
    handle: ci.influencers?.handle,
    platform: ci.influencers?.platform,
    profilePicUrl: ci.influencers?.profile_pic_url,
    tags: ['Fashion', 'Lifestyle'],
    status: ci.status,
    deliverables: ci.deliverables || 'Not specified',
    contentStatus: 'Pending',
    totalPayment: ci.fee_committed ? formatCurrency(ci.fee_committed, userCountry) : 'Not set',
    paid: formatCurrency(30000, userCountry),
    pending: formatCurrency(20000, userCountry),
    notes: ci.notes || '',
    followersCount: ci.influencers?.followers_count,
    videoViewCount: ci.influencers?.video_view_count,
    feeCommitted: ci.fee_committed,
    engagementRate: ci.influencers?.engagement_rate,
  }));

  const assignedInfluencerIds = campaignInfluencers.map((ci: any) => ci.influencer_id);

  const handleEditCampaign = () => {
    setEditCampaignModalOpen(true);
  };

  const handleAddInfluencer = () => {
    setAddInfluencerModalOpen(true);
  };

  const handleBulkEmail = () => {
    navigate(`/email-center?campaignId=${campaignId}`);
  };

  if (!campaignId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div>Campaign ID not found</div>
      </div>
    );
  }

  if (loadingCampaign || loadingInfluencers) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div>Loading campaign data...</div>
      </div>
    );
  }

  if (campaignError || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div>Campaign not found or error loading campaign data</div>
      </div>
    );
  }

  const transformedCampaign = mapDbRowToCampaignDetail(
    campaign,
    userCountry,
    campaignInfluencers.length
  );

  return (
    <div className="w-full bg-gray-50">
      <div className="mx-auto p-6 space-y-6">
        {/* Campaign Header */}
        <CampaignHeader campaign={transformedCampaign} onEdit={handleEditCampaign} />

        {/* Campaign Stats Cards */}
        <CampaignStatsCards
          campaign={transformedCampaign}
          rawCampaign={campaign}
          influencers={transformedInfluencers}
          onBulkEmail={handleBulkEmail}
        />

        {/* Campaign Operations Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <CampaignOperationsTabs
            influencers={transformedInfluencers}
            campaignId={campaignId}
            onAddInfluencer={handleAddInfluencer}
          />
        </div>

        {/* Financial Tabs */}
        <div className="bg-white rounded-lg shadow-sm">
          <CampaignFinancialTabs
            influencers={transformedInfluencers}
            expenses={mockExpenses}
            campaignId={campaignId}
            campaignInfluencers={campaignInfluencers}
          />
        </div>

        {/* Add Influencer Modal */}
        <AddInfluencerModal
          open={addInfluencerModalOpen}
          onOpenChange={setAddInfluencerModalOpen}
          campaignId={campaignId}
          assignedInfluencerIds={assignedInfluencerIds}
          onSelectAddByHandle={() => setAddByHandleModalOpen(true)}
          onSelectImportCsv={() => setImportCsvModalOpen(true)}
        />

        {/* Add Influencer by Handle Modal */}
        <AddInfluencerByHandleModal
          open={addByHandleModalOpen}
          onOpenChange={setAddByHandleModalOpen}
          onSave={() => {}}
          existingInfluencers={[]}
          onBack={() => {
            setAddByHandleModalOpen(false);
            setAddInfluencerModalOpen(true);
          }}
          onImportClick={() => {
            setAddByHandleModalOpen(false);
            setImportCsvModalOpen(true);
          }}
          campaignId={campaignId}
        />

        {/* Import Influencers CSV Modal */}
        <ImportInfluencersModal
          open={importCsvModalOpen}
          onOpenChange={setImportCsvModalOpen}
          onImported={() => {}}
          onBack={() => {
            setImportCsvModalOpen(false);
            setAddInfluencerModalOpen(true);
          }}
          campaignId={campaignId}
        />

        {/* Edit Campaign Modal */}
        <EditCampaignModal
          open={editCampaignModalOpen}
          onOpenChange={setEditCampaignModalOpen}
          campaign={campaign}
        />
      </div>
    </div>
  );
};

export default CampaignDetailView;
